/* ====================================================================
   edj-gift-purchase.js — Lot 7 Batch C
   Custom element <edj-gift-purchase>.

   - Gère l'état (montant, custom, message, to/from, delivery, date, email)
   - Met à jour la preview live (carte, message, total)
   - Au clic CTA : ajoute le produit gift_card au cart via /cart/add.js
     en passant le bon variantId (le variant dont price === amount sélectionné)
     et les line item properties (Recipient name/email, Message, Send on).
   - Si pas de variant gratuit, fallback : ajoute la première variante
     avec un property "Custom amount" (note pour traitement manuel).
   ==================================================================== */

(function () {
  'use strict';

  if (customElements.get('edj-gift-purchase')) return;

  class EdjGiftPurchase extends HTMLElement {
    constructor() {
      super();
      this._variants = [];
      this._initialAmount = 50;
      this._minAmount = 10;
      this._maxAmount = 500;
      this._physicalExtra = 5;
      this._handle = '';
      this._state = {
        amount: 50,
        custom: '',
        to: '',
        from: '',
        message: '',
        delivery: 'email',
        date: '',
        email: '',
      };
    }

    connectedCallback() {
      this._initialAmount = parseInt(this.getAttribute('data-default-amount'), 10) || 50;
      this._minAmount = parseInt(this.getAttribute('data-min-amount'), 10) || 10;
      this._maxAmount = parseInt(this.getAttribute('data-max-amount'), 10) || 500;
      this._physicalExtra = parseInt(this.getAttribute('data-physical-extra'), 10) || 5;
      this._handle = this.getAttribute('data-product-handle') || '';
      this._state.amount = this._initialAmount;

      this._bind();
      this._fetchVariants();
      this._render();
    }

    _bind() {
      // Montants
      this.querySelectorAll('[data-edj-amount]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const v = parseInt(btn.getAttribute('data-edj-amount'), 10);
          this._state.amount = v;
          this._state.custom = '';
          const customInput = this.querySelector('[data-edj-amount-custom]');
          if (customInput) customInput.value = '';
          this._syncAmountButtons();
          this._render();
        });
      });

      const customInput = this.querySelector('[data-edj-amount-custom]');
      if (customInput) {
        customInput.addEventListener('input', () => {
          this._state.custom = customInput.value;
          this._syncAmountButtons();
          this._render();
        });
      }

      // Personnaliser
      const toInput = this.querySelector('[data-edj-to]');
      if (toInput) {
        toInput.addEventListener('input', () => {
          this._state.to = toInput.value;
          this._render();
        });
      }
      const fromInput = this.querySelector('[data-edj-from]');
      if (fromInput) {
        fromInput.addEventListener('input', () => {
          this._state.from = fromInput.value;
          this._render();
        });
      }
      const msgInput = this.querySelector('[data-edj-message]');
      if (msgInput) {
        msgInput.addEventListener('input', () => {
          this._state.message = msgInput.value;
          this._render();
        });
      }

      // Livraison
      this.querySelectorAll('[data-edj-delivery]').forEach((radio) => {
        radio.addEventListener('change', () => {
          if (radio.checked) {
            this._state.delivery = radio.value;
            this._syncDeliveryItems();
            this._toggleConditionalFields();
            this._render();
          }
        });
      });

      const dateInput = this.querySelector('[data-edj-date]');
      if (dateInput) {
        dateInput.addEventListener('input', () => {
          this._state.date = dateInput.value;
        });
      }

      const emailInput = this.querySelector('[data-edj-email]');
      if (emailInput) {
        emailInput.addEventListener('input', () => {
          this._state.email = emailInput.value;
        });
      }

      // CTA
      const cta = this.querySelector('[data-edj-add-cart]');
      if (cta) {
        cta.addEventListener('click', () => this._addToCart());
      }

      // Sync initial
      this._toggleConditionalFields();
    }

    _syncAmountButtons() {
      const finalAmount = this._finalAmount();
      this.querySelectorAll('[data-edj-amount]').forEach((btn) => {
        const v = parseInt(btn.getAttribute('data-edj-amount'), 10);
        const selected = !this._state.custom && v === this._state.amount;
        btn.classList.toggle('is-selected', selected);
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    _syncDeliveryItems() {
      this.querySelectorAll('[data-edj-delivery-item]').forEach((item) => {
        const id = item.getAttribute('data-edj-delivery-item');
        item.classList.toggle('is-selected', id === this._state.delivery);
      });
    }

    _toggleConditionalFields() {
      const dateWrap = this.querySelector('[data-edj-date-wrap]');
      const emailWrap = this.querySelector('[data-edj-email-wrap]');
      if (dateWrap) {
        if (this._state.delivery === 'scheduled') dateWrap.removeAttribute('hidden');
        else dateWrap.setAttribute('hidden', '');
      }
      if (emailWrap) {
        if (this._state.delivery === 'email' || this._state.delivery === 'scheduled') {
          emailWrap.removeAttribute('hidden');
        } else {
          emailWrap.setAttribute('hidden', '');
        }
      }
    }

    _finalAmount() {
      if (this._state.custom) {
        const c = parseInt(this._state.custom, 10);
        if (!Number.isNaN(c) && c > 0) return c;
      }
      return this._state.amount;
    }

    _totalAmount() {
      return this._finalAmount() + (this._state.delivery === 'physical' ? this._physicalExtra : 0);
    }

    _render() {
      const final = this._finalAmount();
      const total = this._totalAmount();

      // Preview carte
      const cardAmount = this.querySelector('[data-edj-card-amount]');
      if (cardAmount) cardAmount.textContent = String(final);

      const cardTo = this.querySelector('[data-edj-card-to]');
      if (cardTo) cardTo.textContent = this._state.to ? this._state.to : '—';

      // Preview message
      const msgText = this.querySelector('[data-edj-msg-text]');
      if (msgText) {
        msgText.textContent = this._state.message
          ? this._state.message
          : 'Pour célébrer ce moment. Avec toute mon affection.';
      }
      const msgFrom = this.querySelector('[data-edj-msg-from]');
      if (msgFrom) msgFrom.textContent = this._state.from ? this._state.from : '—';

      // Total
      const totalEl = this.querySelector('[data-edj-total]');
      if (totalEl) totalEl.textContent = String(total);

      // Compteur message
      const countEl = this.querySelector('[data-edj-message-count]');
      if (countEl) countEl.textContent = String(this._state.message.length);
    }

    async _fetchVariants() {
      if (!this._handle) return;
      try {
        const res = await fetch('/products/' + encodeURIComponent(this._handle) + '.js', {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        this._variants = (data.variants || []).map((v) => ({
          id: v.id,
          // /products/:handle.js renvoie price en CENTIMES
          priceEuros: Math.round((v.price || 0) / 100),
          title: v.title,
        }));
      } catch (err) {
        // silent — on garde fallback initial-variant-id
      }
    }

    _findVariantForAmount(amount) {
      if (!this._variants.length) {
        const initial = this.getAttribute('data-initial-variant-id');
        return initial ? parseInt(initial, 10) : null;
      }
      // Match exact d'abord
      const exact = this._variants.find((v) => v.priceEuros === amount);
      if (exact) return exact.id;
      // Sinon variant max ≤ amount, fallback sur le plus petit
      const lowerOrEqual = this._variants
        .filter((v) => v.priceEuros <= amount)
        .sort((a, b) => b.priceEuros - a.priceEuros)[0];
      if (lowerOrEqual) return lowerOrEqual.id;
      return this._variants.sort((a, b) => a.priceEuros - b.priceEuros)[0].id;
    }

    _showMsg(text, type) {
      const el = this.querySelector('[data-edj-cta-msg]');
      if (!el) return;
      el.textContent = text;
      el.classList.remove('is-success', 'is-error');
      if (type) el.classList.add('is-' + type);
    }

    _validate() {
      const a = this._finalAmount();
      if (a < this._minAmount) {
        this._showMsg('Montant minimum : ' + this._minAmount + ' €', 'error');
        return false;
      }
      if (a > this._maxAmount) {
        this._showMsg('Montant maximum : ' + this._maxAmount + ' €', 'error');
        return false;
      }
      if (this._state.delivery === 'email' || this._state.delivery === 'scheduled') {
        if (!this._state.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this._state.email)) {
          this._showMsg('Email du destinataire requis.', 'error');
          return false;
        }
      }
      if (this._state.delivery === 'scheduled' && !this._state.date) {
        this._showMsg('Date d’envoi requise.', 'error');
        return false;
      }
      return true;
    }

    async _addToCart() {
      if (!this._handle) {
        this._showMsg('Configuration produit manquante.', 'error');
        return;
      }
      if (!this._validate()) return;

      const final = this._finalAmount();
      const variantId = this._findVariantForAmount(final);
      if (!variantId) {
        this._showMsg('Variant carte cadeau introuvable.', 'error');
        return;
      }

      const cta = this.querySelector('[data-edj-add-cart]');
      if (cta) cta.classList.add('is-loading');
      this._showMsg('');

      const properties = {};
      if (this._state.to) properties['Pour'] = this._state.to;
      if (this._state.from) properties['De la part de'] = this._state.from;
      if (this._state.message) properties['Message'] = this._state.message;
      if (this._state.email) properties['Recipient email'] = this._state.email;
      properties['Mode de livraison'] = this._deliveryLabel();
      if (this._state.delivery === 'scheduled' && this._state.date) {
        properties['Date d’envoi'] = this._state.date;
      }
      // Si custom amount différent du variant
      const varInfo = this._variants.find((v) => v.id === variantId);
      if (varInfo && varInfo.priceEuros !== final) {
        properties['Montant souhaité'] = final + ' €';
      }
      if (this._state.delivery === 'physical') {
        properties['Supplément carte physique'] = this._physicalExtra + ' €';
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties,
              },
            ],
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          this._showMsg(err.description || err.message || 'Erreur lors de l’ajout au panier.', 'error');
          return;
        }
        this._showMsg('Carte cadeau ajoutée au panier.', 'success');
        // Notifier le cart drawer global (Lot 5)
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { source: 'gift-purchase' } }));
        // Optionnel : redirection vers /cart après 800ms
        setTimeout(() => {
          window.location.href = '/cart';
        }, 900);
      } catch (err) {
        this._showMsg('Erreur réseau, réessayez.', 'error');
      } finally {
        if (cta) cta.classList.remove('is-loading');
      }
    }

    _deliveryLabel() {
      switch (this._state.delivery) {
        case 'scheduled': return 'Email programmé';
        case 'physical': return 'Carte physique';
        default: return 'Email immédiat';
      }
    }
  }

  customElements.define('edj-gift-purchase', EdjGiftPurchase);
})();
